<template>
    <page-loading v-if="loading"></page-loading>
    <form-container v-else>
        <page-header class="mb-6 lg:mb-12" data-test="message-standard-header">
            <template v-slot:subtitle>
                <a
                    :href="`/settings/teams/${teamId}#/message-standards`"
                    class="no-underline! text-sky-600! text-base font-normal"
                >
                    <font-awesome-icon widthAuto class="mr-1" :icon="faArrowLeft"></font-awesome-icon>
                    All Brand Standards
                </a>
            </template>

            Default Standards
        </page-header>
        <div class="grid grid-cols-12">
            <page-sidebar appearance="nested" :links="sidebarLinks" data-test="message-standard-sidebar"></page-sidebar>

            <form-wrapper
                v-if="!targetsView"
                :sections="[
                    { id: 'marketing-rules', label: 'Marketing' },
                    { id: 'transactional-rules', label: 'Transactional' },
                ]"
                :hideNav="true"
            >
                <message-standard-rules-form
                    ref="rulesForm"
                    v-model="messageStandard"
                    :showRulesInitially="true"
                    v-model:marketingEnabled="marketingRulesEnabled"
                    v-model:transactionalEnabled="transactionalRulesEnabled"
                />

                <vx-sticky-footer>
                    <vx-button
                        :href="`/settings/teams/${teamId}#/message-standards`"
                        class="w-20"
                        size="lg"
                        color="muted"
                    >
                        Cancel
                    </vx-button>
                    <vx-button
                        @click="submit()"
                        data-test="submit-button"
                        class="grow"
                        size="lg"
                        color="primary"
                        :loading="processing"
                    >
                        Update Default Standard
                    </vx-button>
                </vx-sticky-footer>
            </form-wrapper>

            <div v-if="targetsView" class="col-span-12 lg:col-span-8 lg:col-start-3">
                <vx-alert color="slate" shadow :closeable="false">
                    <strong>Default Standards</strong> apply when no manual Brand Standard is set.
                </vx-alert>
            </div>
        </div>
    </form-container>
</template>

<script setup lang="ts">
import { VxAlert, VxButton, VxStickyFooter } from '@voxie/frontend-components';
import { ref, onMounted, nextTick, computed } from 'vue';
import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons';
import { messageStandardsClient } from '~/services';
import FormWrapper from '~/components/general/FormWrapper.vue';
import FormContainer from '~/components/general/FormContainer.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import PageSidebar from '~/components/general/PageSidebar.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import { defaultMessageStandard, readMessageStandard } from './useMessageStandardDetails';
import { useToasted } from '~/composables/useToasted';
import { catchRequestError } from '~/utils/request';
import MessageStandardRulesForm from './MessageStandardRulesForm.vue';
import { useFormSubmission } from '~/composables/useFormSubmission';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const teamId = Spark.state.currentTeam.id;
const toasted = useToasted();

const targetsView = ref(false);

const sidebarLinks = computed(() => [
    { active: !targetsView.value, onClick: () => (targetsView.value = false), href: '#', label: 'Details' },
    {
        onClick: () => (targetsView.value = true),
        href: '#',
        label: 'Targets',
        active: targetsView.value,
    },
]);

const messageStandard = ref(defaultMessageStandard());
const loading = ref(true);
const rulesForm = ref<InstanceType<typeof MessageStandardRulesForm>>();
const marketingRulesEnabled = ref(false);
const transactionalRulesEnabled = ref(false);

onMounted(async () => {
    try {
        loading.value = true;
        const response = await messageStandardsClient.getDefaultMessageStandard({ teamId });
        messageStandard.value = readMessageStandard(response.data);

        loading.value = false;

        await nextTick();
        if (rulesForm.value) {
            marketingRulesEnabled.value = Boolean(rulesForm.value.marketingCount);
            transactionalRulesEnabled.value = Boolean(rulesForm.value.transactionalCount);
        }
    } catch (e) {
        toasted.error(catchRequestError(e));
    }
});

const save = async () => {
    const data = { ...messageStandard.value };

    if (!marketingRulesEnabled.value) {
        data.marketing_rules = undefined;
    }

    if (messageStandard.value.transactional_config !== null) {
        data.transactional_rules = undefined;
    }

    if (!transactionalRulesEnabled.value) {
        data.transactional_config = undefined;
        data.transactional_rules = undefined;
    }

    const response = await messageStandardsClient.updateDefaultMessageStandard({ teamId }, data);

    messageStandard.value = readMessageStandard(response.data);
};

const { submit, processing } = useFormSubmission({
    data: messageStandard,
    validation: {},
    preserveScroll: () => true,
    submit: save,
    reset: () => {},
    notification: () => ({
        message: 'Default Brand Standard updated successfully',
    }),
});
</script>
